// Unified create/view/edit dialog for blog posts.
// Extracted from BlogsPage.js.
//
// Prop count is non-trivial (this is a complex dialog). To avoid ballooning
// further, two compound callbacks collapse what were inline arrow functions
// in the original:
//   - onCreateNew: resets the editor state for a new blog (12 setters in the
//                  original; the parent owns the reset semantics).
//   - onSwitchToEdit: swaps mode from 'view' to 'edit'.

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Close, ContentCopy, Add, Edit } from '@mui/icons-material';
import { generateOfferElementHTML } from '../utils/blogHtmlGenerators';

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text);
};

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {'create' | 'view' | 'edit'} props.mode
 * @param {() => void} props.onClose
 * @param {object} props.blogForm
 * @param {(form: object) => void} props.setBlogForm
 * @param {object | null} props.selectedOffer
 * @param {object | null} props.selectedBlogPost
 * @param {string} props.selectedElementHtml
 * @param {File | null} props.selectedFile
 * @param {boolean} props.uploading
 * @param {boolean} props.editing
 * @param {(e: Event) => void} props.onFileSelect
 * @param {() => void} props.onFileUpload
 * @param {() => void} props.onUpdateBlog
 * @param {() => void} props.onCreateNew - resets state for a new blog (parent owns).
 * @param {() => void} props.onSwitchToEdit - swap mode from 'view' to 'edit'.
 * @param {(offer: object) => string} props.generateAffiliateLink - returns the offer's affiliate URL.
 */
const BlogPostDialog = ({
  open,
  mode,
  onClose,
  blogForm,
  setBlogForm,
  selectedOffer,
  selectedBlogPost,
  selectedElementHtml,
  selectedFile,
  uploading,
  editing,
  onFileSelect,
  onFileUpload,
  onUpdateBlog,
  onCreateNew,
  onSwitchToEdit,
  generateAffiliateLink,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="lg"
    fullWidth
  >
    <DialogTitle>
      {mode === 'create' && `Create Blog Post - ${selectedOffer?.title}`}
      {mode === 'view' && selectedBlogPost?.title}
      {mode === 'edit' && 'Edit Blog Post'}
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{ position: 'absolute', right: 8, top: 8 }}
      >
        <Close />
      </IconButton>
    </DialogTitle>
    <DialogContent>
      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Left Side - Blog Input Fields and Offer Element */}
        <Grid item xs={12} md={6}>
          {/* Three Input Rows */}
          <Box sx={{ mb: 3 }}>
            {/* Blog Name - always shown */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              BLOG NAME
            </Typography>
            <TextField
              fullWidth
              value={blogForm.title}
              onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })}
              placeholder="How to get Protein in Your Body"
              sx={{ mb: 2 }}
              InputProps={{ readOnly: mode === 'view' }}
            />

            {/* Offer Name - always shown, auto-fill from selected offer or post data */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              OFFER NAME
            </Typography>
            <TextField
              fullWidth
              value={selectedOffer?.title || (selectedBlogPost?.offerId?.title) || 'No Offer Associated'}
              disabled
              sx={{ mb: 2 }}
            />

            {/* Affiliate Link - always shown, generate or show existing */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              AFFILIATE LINK
            </Typography>
            <TextField
              fullWidth
              value={selectedOffer ? generateAffiliateLink(selectedOffer) : (selectedBlogPost?.offerId ? generateAffiliateLink({ _id: selectedBlogPost.offerId._id || selectedBlogPost.offerId }) : '')}
              disabled
              sx={{
                mb: 2,
                '& .MuiInputBase-input': {
                  fontFamily: 'monospace',
                  fontSize: '12px'
                }
              }}
              InputProps={{
                endAdornment: (selectedOffer || selectedBlogPost?.offerId) && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      const link = selectedOffer
                        ? generateAffiliateLink(selectedOffer)
                        : generateAffiliateLink({ _id: selectedBlogPost.offerId._id || selectedBlogPost.offerId });
                      copyToClipboard(link);
                    }}
                  >
                    <ContentCopy />
                  </IconButton>
                )
              }}
            />
          </Box>

          {/* Offer Element Preview - shown in all modes */}
          {(selectedOffer || selectedBlogPost?.offerId) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                OFFER ELEMENT PREVIEW
              </Typography>
              <Box
                sx={{
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  p: 1,
                  backgroundColor: '#f9f9f9',
                  width: '100%'
                }}
                dangerouslySetInnerHTML={{
                  __html: selectedElementHtml || (selectedOffer
                    ? generateOfferElementHTML(selectedOffer, generateAffiliateLink(selectedOffer), blogForm.title)
                    : generateOfferElementHTML(
                      selectedBlogPost.offerId,
                      generateAffiliateLink({ _id: selectedBlogPost.offerId._id || selectedBlogPost.offerId }),
                      blogForm.title
                    ))
                }}
              />
            </Box>
          )}

          {/* Blog Content Display - shown in view mode */}
          {mode === 'view' && selectedBlogPost && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                BLOG CONTENT
              </Typography>
              <Box
                sx={{
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  p: 2,
                  backgroundColor: '#f9f9f9',
                  minHeight: '200px',
                  maxHeight: '400px',
                  overflow: 'auto'
                }}
              >
                {(() => {
                  try {
                    const elements = JSON.parse(selectedBlogPost.content);
                    if (Array.isArray(elements)) {
                      return elements.map((element, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                          {element.type === 'title' && (
                            <Typography variant="h5" sx={{ mb: 1 }}>
                              {element.content}
                            </Typography>
                          )}
                          {element.type === 'text' && (
                            <Typography variant="body1" sx={{ mb: 1 }}>
                              {element.content}
                            </Typography>
                          )}
                          {element.type === 'media' && element.mediaUrl && (
                            <Box sx={{ textAlign: 'center', mb: 1 }}>
                              {element.mediaUrl.match(/\.(mp4|avi|mov|wmv|flv|webm)($|\?)/i) ? (
                                <video
                                  src={element.mediaUrl}
                                  controls
                                  style={{ maxWidth: '100%', maxHeight: '200px' }}
                                />
                              ) : (
                                <img
                                  src={element.mediaUrl}
                                  alt="Blog media"
                                  style={{ maxWidth: '100%', maxHeight: '200px' }}
                                />
                              )}
                            </Box>
                          )}
                          {element.type === 'offer' && element.htmlContent && (
                            <Box
                              dangerouslySetInnerHTML={{
                                __html: element.htmlContent
                              }}
                            />
                          )}
                        </Box>
                      ));
                    }
                  } catch (error) {
                    // Fallback to plain text
                    return (
                      <Typography variant="body1">
                        {selectedBlogPost.content}
                      </Typography>
                    );
                  }
                  return null;
                })()}
              </Box>
            </Box>
          )}
        </Grid>

        {/* Right Side - Media Uploader and Details */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            Media Upload
          </Typography>



          {/* File Upload Section - hide in view mode */}
          {mode !== 'view' && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Upload image or video file
              </Typography>

              {/* File Input */}
              <input
                type="file"
                accept="image/*,video/*"
                onChange={onFileSelect}
                style={{ display: 'none' }}
                id="media-file-input"
              />
              <label htmlFor="media-file-input">
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                  sx={{ mb: 1 }}
                >
                  Choose image or video file
                </Button>
              </label>

              {/* Selected File Display */}
              {selectedFile && (
                <Box sx={{
                  mb: 1,
                  p: 2,
                  backgroundColor: '#e3f2fd',
                  borderRadius: 1,
                  border: '1px solid #1976d2'
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                    📁 {selectedFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB | Type: {selectedFile.type}
                  </Typography>
                </Box>
              )}

              {/* Upload Button */}
              <Button
                variant="contained"
                onClick={onFileUpload}
                disabled={!selectedFile || uploading}
                fullWidth
                sx={{ mb: 1 }}
              >
                {uploading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Uploading...
                  </>
                ) : (
                  'Upload file'
                )}
              </Button>




            </Box>
          )}



          {/* Media Preview */}
          {blogForm.featuredImage && (
            <Box sx={{ mb: 2, textAlign: 'center' }}>
              {blogForm.featuredImage.match(/\.(mp4|avi|mov|wmv|flv|webm)($|\?)/i) ? (
                <video
                  src={blogForm.featuredImage}
                  controls
                  style={{
                    maxWidth: '100%',
                    maxHeight: '300px',
                    borderRadius: '8px'
                  }}
                />
              ) : (
                <img
                  src={blogForm.featuredImage}
                  alt="Preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '300px',
                    borderRadius: '8px'
                  }}
                />
              )}
            </Box>
          )}

          {/* Media Title */}
          <TextField
            fullWidth
            label="Title"
            value={blogForm.mediaTitle}
            onChange={(e) => setBlogForm({ ...blogForm, mediaTitle: e.target.value })}
            placeholder="Enter title for the media"
            sx={{ mb: 2 }}
            InputProps={{ readOnly: mode === 'view' }}
          />

          {/* Media Description */}
          <TextField
            fullWidth
            label="Description"
            multiline
            rows={4}
            value={blogForm.mediaDescription}
            onChange={(e) => setBlogForm({ ...blogForm, mediaDescription: e.target.value })}
            placeholder="Enter description for the media"
            sx={{ mb: 2 }}
            InputProps={{ readOnly: mode === 'view' }}
          />

          {/* Tags */}
          <TextField
            fullWidth
            label="Tags (comma separated)"
            value={blogForm.tags.join(', ')}
            onChange={(e) => setBlogForm({
              ...blogForm,
              tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)
            })}
            placeholder="health, fitness, protein"
            InputProps={{ readOnly: mode === 'view' }}
          />
        </Grid>
      </Grid>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>
        {mode === 'view' ? 'Close' : 'Cancel'}
      </Button>

      {/* Create button for create mode - REMOVED to prevent duplicate creation */}

      {/* Create new blog button for view mode */}
      {mode === 'view' && selectedBlogPost && (
        <Button
          variant="outlined"
          onClick={onCreateNew}
          startIcon={<Add />}
        >
          Create New Blog
        </Button>
      )}

      {/* Edit button for view mode */}
      {mode === 'view' && selectedBlogPost && (
        <Button
          variant="contained"
          onClick={onSwitchToEdit}
          startIcon={<Edit />}
        >
          Edit
        </Button>
      )}

      {/* Update button for edit mode */}
      {mode === 'edit' && (
        <Button
          onClick={onUpdateBlog}
          variant="contained"
          disabled={editing || !blogForm.title}
          startIcon={editing ? <CircularProgress size={20} /> : <Edit />}
        >
          {editing ? 'Updating...' : 'Update Blog Post'}
        </Button>
      )}
    </DialogActions>
  </Dialog>
);

export default BlogPostDialog;
